import { API_CONFIG } from '../config/api.config';

/**
 * API 请求客户端
 * 提供统一的 HTTP 请求方法
 */
class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
  }

  /**
   * 获取完整的 API URL
   * @param {string} endpoint - API 端点
   * @returns {string} 完整的 URL
   */
  getUrl(endpoint) {
    return `${this.baseURL}${endpoint}`;
  }

  /**
   * 获取请求头（包含 token）
   * @param {Object} customHeaders - 自定义请求头
   * @returns {Object} 请求头
   */
  getHeaders(customHeaders = {}) {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * 发送 GET 请求
   * @param {string} endpoint - API 端点
   * @param {Object} options - 请求选项
   * @returns {Promise} 请求响应
   */
  async get(endpoint, options = {}) {
    const url = this.getUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(options.headers),
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP 错误! 状态: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API 请求失败:', error);
      throw error;
    }
  }

  /**
   * 发送 POST 请求
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求数据
   * @param {Object} options - 请求选项
   * @returns {Promise} 请求响应
   */
  async post(endpoint, data = {}, options = {}) {
    const url = this.getUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      });

      // 先解析响应体，获取可能的错误信息
      const result = await response.json();

      if (!response.ok) {
        // 返回带有错误的响应对象，让调用方处理
        return result;
      }

      return result;
    } catch (error) {
      console.error('API 请求失败:', error);
      throw error;
    }
  }

  /**
   * 发送 PUT 请求
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求数据
   * @param {Object} options - 请求选项
   * @returns {Promise} 请求响应
   */
  async put(endpoint, data = {}, options = {}) {
    const url = this.getUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP 错误! 状态: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API 请求失败:', error);
      throw error;
    }
  }

  /**
   * 发送 DELETE 请求
   * @param {string} endpoint - API 端点
   * @param {Object} options - 请求选项
   * @returns {Promise} 请求响应
   */
  async delete(endpoint, options = {}) {
    const url = this.getUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders(options.headers),
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP 错误! 状态: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API 请求失败:', error);
      throw error;
    }
  }
}

// 导出单例实例
export const apiClient = new ApiClient();
